import { Observable } from 'rxjs';

import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { ApiUrlInjectionToken } from '@mitsubishi/shared/data-access';
import { getRequestTraceHttpHeaders } from '@mitsubishi/shared/request-tracing';

import { FormlyFieldConfig } from '@ngx-formly/core';

import { Datasheet, EditDatasheetRequest } from '../models';

type UpdatedDataSheetPdfSettingsPayload = { datasheetNote: string; sectionIdList: string[] };

@Injectable({
  providedIn: 'root',
})
export class DatasheetService {
  private readonly baseUrl: string;

  constructor(
    private http: HttpClient,
    @Inject(ApiUrlInjectionToken) apiServerUrl: string
  ) {
    this.baseUrl = apiServerUrl;
  }

  getSelectionUnitDatasheet(projectId: string, unitId: string, model: EditDatasheetRequest): Observable<Datasheet> {
    return this.http.post<Datasheet>(
      `${this.baseUrl}/project/${projectId}/selection/unit/${unitId}/datasheet`,
      model ?? {}
    );
  }

  getStudyUnitDatasheet(
    projectId: string,
    performanceId: string,
    unitId: string,
    model: EditDatasheetRequest
  ): Observable<Datasheet> {
    return this.http.post<Datasheet>(
      `${this.baseUrl}/project/${projectId}/study-unit/${unitId}/performance/${performanceId}/datasheet-preview`,
      model ?? {}
    );
  }

  generateSelectionUnitDatasheetPDF(
    projectId: string,
    selectionId: string,
    unitId: string,
    requestTraceId?: string
  ): Observable<FormlyFieldConfig[]> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.post<FormlyFieldConfig[]>(
      `${this.baseUrl}/project/${projectId}/selection/${selectionId}/unit/${unitId}/datasheet/generate`,
      {},
      {
        headers,
      }
    );
  }

  generateStudyUnitDatasheetPDF(
    projectId: string,
    studyId: string,
    studySubcategoryId: string,
    performanceId: string,
    unitId: string,
    requestTraceId?: string
  ): Observable<FormlyFieldConfig[]> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.post<FormlyFieldConfig[]>(
      `${this.baseUrl}/project/${projectId}/study/${studyId}/subcategory/${studySubcategoryId}/unit/${unitId}/performance/${performanceId}/datasheet/generate`,
      {},
      {
        headers,
      }
    );
  }

  exportUnitDatasheetPDF(fileName: string, token?: string | null): Observable<{ url: string }> {
    const params = {};
    if (token) {
      params['token'] = token;
    }
    return this.http.get<{ url: string }>(`${this.baseUrl}/project/selection/unit/datasheet/get/${fileName}`, {
      params,
    });
  }

  calculateSelectionDatasheet(projectId: string, unitId: string): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/project/${projectId}/selection/unit/${unitId}/performance-detail`, {});
  }

  calculateStudyDatasheet({
    projectId,
    unitId,
    performanceId,
  }: {
    projectId: string;
    unitId: string;
    performanceId: string;
  }): Observable<null> {
    return this.http.post<null>(
      `${this.baseUrl}/project/${projectId}/study-unit/${unitId}/performance/${performanceId}/detail-calculation`,
      {}
    );
  }

  saveSelectionDatasheet(projectId: string, unitId: string, model: EditDatasheetRequest): Observable<null> {
    return this.http.post<null>(
      `${this.baseUrl}/project/${projectId}/selection/unit/${unitId}/save-datasheet`,
      model ?? {}
    );
  }

  saveStudyDatasheet(
    projectId: string,
    performanceId: string,
    unitId: string,
    model: EditDatasheetRequest
  ): Observable<null> {
    return this.http.post<null>(
      `${this.baseUrl}/project/${projectId}/study-unit/${unitId}/performance/${performanceId}/datasheet`,
      model ?? {}
    );
  }

  updateSelectionSectionPdfVisibility(
    projectId: string,
    unitId: string,
    payload: UpdatedDataSheetPdfSettingsPayload
  ): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/project/${projectId}/selection/unit/${unitId}/datasheet`, payload);
  }

  updateStudySectionPdfVisibility(
    projectId: string,
    workingConditionId: string,
    unitId: string,
    payload: UpdatedDataSheetPdfSettingsPayload
  ): Observable<null> {
    return this.http.patch<null>(
      `${this.baseUrl}/project/${projectId}/study-unit/${unitId}/working-condition/${workingConditionId}/datasheet`,
      payload
    );
  }

  downloadPartialLoadsSelection(
    projectId: string,
    unitId: string,
    model: EditDatasheetRequest
  ): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.baseUrl}/project/${projectId}/selection/unit/${unitId}/partial-loads`, model ?? {}, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  downloadPartialLoadsStudy(
    projectId: string,
    performanceId: string,
    unitId: string,
    model: EditDatasheetRequest
  ): Observable<HttpResponse<Blob>> {
    return this.http.post(
      `${this.baseUrl}/project/${projectId}/study-unit/${unitId}/performance/${performanceId}/partial-loads-export`,
      model ?? {},
      { observe: 'response', responseType: 'blob' }
    );
  }

  downloadPartialLoadsEa(unitId: string, model: EditDatasheetRequest): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.baseUrl}/ea-units/${unitId}/partial-loads`, model ?? {}, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  getEaUnitDatasheet(projectId: string, unitId: string, model: EditDatasheetRequest): Observable<Datasheet> {
    return this.http.post<Datasheet>(
      `${this.baseUrl}/project/${projectId}/ea-unit/${unitId}/datasheet`,
      model ?? {}
    );
  }

  saveEaUnitDatasheet(projectId: string, unitId: string, model: EditDatasheetRequest): Observable<null> {
    return this.http.post<null>(
      `${this.baseUrl}/project/${projectId}/ea-unit/${unitId}/save-datasheet`,
      model ?? {}
    );
  }

  calculateEaDatasheet({ unitId }: { unitId: string }): Observable<null> {
    return this.http.post<null>(
      `${this.baseUrl}/ea-units/${unitId}/performance-detail`,
      {}
    );
  }

  generateEaUnitDatasheetPDF(
    projectId: string,
    energyAnalysesId: string,
    unitId: string,
    requestTraceId?: string
  ): Observable<FormlyFieldConfig[]> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);
    return this.http.post<FormlyFieldConfig[]>(
      `${this.baseUrl}/project/${projectId}/ea/${energyAnalysesId}/unit/${unitId}/datasheet/generate`,
      {},
      { headers }
    );
  }

  updateEaSectionPdfVisibility(
    projectId: string,
    unitId: string,
    payload: UpdatedDataSheetPdfSettingsPayload
  ): Observable<null> {
    return this.http.patch<null>(
      `${this.baseUrl}/project/${projectId}/ea-unit/${unitId}/datasheet`,
      payload
    );
  }
}
